import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { completeTask } from '@/approvals/api'
import { isForbidden } from '@/analytics/reportKit'
import { StateView } from '@/card/record/StateView'
import { listPath } from '@/card/recordHelpers'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { fetchCalendar, moveEvent, type CalendarEvent, type People } from './api'
import { MeetingFormModal, TaskFormModal, usePeople } from './ActivityForms'
import {
  MINUTES_IN_DAY,
  SNAP_MINUTES,
  addDays,
  atMinutes,
  format,
  fromLocalInput,
  minutesOfDay,
  rangeOf,
  sameDay,
  shift,
  startOfDay,
  time,
  toLocalInput,
  type CalendarView,
} from './dates'
import '@/styles/calendar.css'

/**
 * Календарь задач и встреч (#113).
 *
 * ## Чьи календари
 *
 * Сервер отдаёт всех работающих с флагом `viewable` — можно ли открыть календарь человека
 * (область `sales.activity:read`: своё, свой отдел, все). Выбор людей предлагает только
 * открываемых; пресет «Мой отдел» — это `myDepartment` с сервера, а не своя выборка по
 * отделу на клиенте. Чужой вне области сервер отвергает 403 целиком, а не молча выкидывает:
 * календарь, где человека тихо нет, читается как «он свободен».
 *
 * ## Перенос — домен
 *
 * Перетаскивание зовёт `POST /calendar/events/{id}/move`: встреча сохраняет длительность,
 * закрытая отказывает, чужая вне области правки — 404. Можно ли тащить, решает флаг
 * `editable` с сервера. Для клавиатуры у выбранного события есть поле «Перенести на».
 *
 * ## Время — часы смотрящего
 *
 * Сетка дня — стенные часы браузера; на сервер уходит момент. Пересечения встреч считает
 * сервер (`conflict`): клиент видит только загруженный период и пропустил бы встречу,
 * начавшуюся до него.
 */

const HOUR_PX = 48
/** Shortest block drawn: 45 minutes = 36 px, room for the title and the time. */
const MIN_VISIBLE_MINUTES = 45
const SLOT_PX = HOUR_PX / (60 / SNAP_MINUTES)
const SCROLL_TO_HOUR = 8

type Creating = { kind: 'task' | 'meeting'; at: Date | null } | null
type Editing = CalendarEvent | null

export function CalendarPage() {
  const { t, locale, user } = useApp()
  const queryClient = useQueryClient()
  const [view, setView] = useState<CalendarView>('week')
  const [anchor, setAnchor] = useState(() => new Date())
  const [selectedPeople, setSelectedPeople] = useState<string[] | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [slot, setSlot] = useState<Date | null>(null)
  const [creating, setCreating] = useState<Creating>(null)
  const [editing, setEditing] = useState<Editing>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const people = usePeople()
  const me = user?.employeeId ?? people.data?.me ?? null
  // Пока человек не выбрал сам — свой календарь.
  const chosen = selectedPeople ?? (me ? [me] : [])
  const range = rangeOf(view, anchor)

  const calendar = useQuery({
    queryKey: ['calendar', range.from.toISOString(), range.to.toISOString(), chosen.join(',')],
    queryFn: ({ signal }) => fetchCalendar(range.from, range.to, chosen, signal),
    enabled: chosen.length > 0,
    retry: false,
  })

  const move = useMutation({
    mutationFn: ({ id, at }: { id: string; at: Date }) => moveEvent(id, at),
    onSuccess: () => {
      setNotice(null)
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
    },
    onError: (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  const complete = useMutation({
    mutationFn: (id: string) => completeTask(id),
    onSuccess: () => {
      setNotice(null)
      void queryClient.invalidateQueries({ queryKey: ['calendar'] })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks'] })
    },
    onError: (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  const events = calendar.data?.events ?? []
  const selected = events.find((event) => event.id === selectedId) ?? null
  const mayCreate = people.data?.mayCreate ?? false
  const colorOf = useColors(chosen)
  const several = chosen.length > 1

  const head = (
    <div className="page-head">
      <h1 className="h1">{t('cal.title')}</h1>
      <div className="seg" role="group" aria-label={t('cal.view')}>
        {(['day', 'week', 'month'] as const).map((code) => (
          <button key={code} type="button" aria-pressed={view === code} onClick={() => setView(code)}>
            {t(`cal.view.${code}`)}
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 4 }}>
        <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('cal.prev')}
                onClick={() => setAnchor((was) => shift(view, was, -1))}>
          <Icon name="arrow-left" size="sm" />
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAnchor(new Date())}>
          {t('cal.today')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('cal.next')}
                onClick={() => setAnchor((was) => shift(view, was, 1))}>
          <Icon name="arrow-right" size="sm" />
        </button>
      </div>
      <span className="cal-head-period" aria-live="polite">{periodLabel(view, anchor, range.days, locale)}</span>
      <span className="spacer" />
      {mayCreate && (
        <>
          <button type="button" className="btn btn-secondary" onClick={() => setCreating({ kind: 'task', at: null })}>
            <Icon name="plus" size="sm" />
            {t('cal.newTask')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setCreating({ kind: 'meeting', at: null })}>
            <Icon name="plus" size="sm" />
            {t('cal.newMeeting')}
          </button>
        </>
      )}
    </div>
  )

  if (people.error) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        {head}
        {isForbidden(people.error) ? (
          <StateView kind="forbidden" boxed title={t('cal.forbidden.title')} description={t('cal.forbidden.text')} />
        ) : (
          <StateView kind="error" boxed title={t('cal.error.title')}
                     description={people.error instanceof ApiError ? people.error.message : t('app.error')} />
        )}
      </div>
    )
  }

  return (
    <div className="stack" style={{ gap: 14 }}>
      {head}
      {notice && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">{notice}</span>
        </div>
      )}

      <PeopleFilter
        people={people.data}
        me={me}
        chosen={chosen}
        colorOf={colorOf}
        onChange={(next) => setSelectedPeople(next)}
      />

      {slot && mayCreate && (
        <div className="cal-slot-menu panel panel-b" role="group" aria-label={t('cal.slot.title')}>
          <span className="t-sm">{t('cal.slot.text', { when: slotLabel(slot, locale) })}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setCreating({ kind: 'task', at: slot }); setSlot(null) }}>
            {t('cal.slot.task')}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setCreating({ kind: 'meeting', at: slot }); setSlot(null) }}>
            {t('cal.slot.meeting')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSlot(null)}>{t('card.cancel')}</button>
        </div>
      )}

      {chosen.length === 0 ? (
        <StateView kind="setup" boxed title={t('cal.noPeople.title')} description={t('cal.noPeople.text')} />
      ) : calendar.error ? (
        isForbidden(calendar.error) ? (
          <StateView kind="forbidden" boxed title={t('cal.forbidden.title')} description={t('cal.forbiddenPeople.text')} />
        ) : (
          <StateView kind="error" boxed title={t('cal.error.title')}
                     description={calendar.error instanceof ApiError ? calendar.error.message : t('app.error')}
                     actions={<button className="btn btn-primary btn-sm" type="button" onClick={() => void calendar.refetch()}>{t('state.retry')}</button>} />
        )
      ) : (
        <div className={selected ? 'cal-layout has-side' : 'cal-layout'}>
          <section className="panel cal-grid-panel" aria-label={t('cal.grid')} aria-busy={calendar.isLoading || move.isPending || undefined}>
            {view === 'month' ? (
              <MonthGrid
                days={range.days}
                anchor={anchor}
                events={events}
                selectedId={selectedId}
                several={several}
                colorOf={colorOf}
                onSelect={setSelectedId}
                onDay={(day) => { setAnchor(day); setView('day') }}
                onDrop={(id, day) => {
                  const event = events.find((candidate) => candidate.id === id)
                  if (!event) return
                  const start = new Date(event.start)
                  const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), start.getHours(), start.getMinutes())
                  if (at.getTime() !== start.getTime()) move.mutate({ id, at })
                }}
              />
            ) : (
              <TimeGrid
                days={range.days}
                events={events}
                selectedId={selectedId}
                several={several}
                colorOf={colorOf}
                onSelect={setSelectedId}
                onSlot={mayCreate ? setSlot : undefined}
                onDrop={(id, at) => {
                  const event = events.find((candidate) => candidate.id === id)
                  if (event && new Date(event.start).getTime() !== at.getTime()) move.mutate({ id, at })
                }}
              />
            )}
            {calendar.isSuccess && events.length === 0 && (
              <div className="panel-f muted">{t('cal.empty')}</div>
            )}
          </section>
          {selected && (
            <EventDetails
              event={selected}
              me={me}
              busy={move.isPending || complete.isPending}
              onClose={() => setSelectedId(null)}
              onMove={(at) => move.mutate({ id: selected.id, at })}
              onEdit={() => setEditing(selected)}
              onComplete={() => complete.mutate(selected.id)}
            />
          )}
        </div>
      )}

      {creating?.kind === 'task' && (
        <TaskFormModal defaultDue={creating.at} onClose={() => setCreating(null)} />
      )}
      {creating?.kind === 'meeting' && (
        <MeetingFormModal defaultStart={creating.at} onClose={() => setCreating(null)} />
      )}
      {editing?.kind === 'meeting' && <MeetingFormModal meeting={editing} onClose={() => setEditing(null)} />}
      {editing && editing.kind !== 'meeting' && <TaskFormModal task={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

// ------------------------------------------------------------------ люди

function PeopleFilter({
  people,
  me,
  chosen,
  colorOf,
  onChange,
}: {
  people: People | undefined
  me: string | null
  chosen: string[]
  colorOf: (id: string) => string
  onChange: (next: string[]) => void
}) {
  const { t } = useApp()
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const viewable = (people?.people ?? []).filter((person) => person.viewable)
  const department = people?.myDepartment ?? []
  const names = new Map((people?.people ?? []).map((person) => [person.id, person.name ?? person.id]))
  const onlyMe = me != null && chosen.length === 1 && chosen[0] === me
  const isDepartment = department.length > 1 && sameSet(chosen, department)
  const needle = filter.trim().toLowerCase()

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="toolbar">
        {me && (
          <button type="button" className="chip" aria-pressed={onlyMe} onClick={() => onChange([me])}>
            <Icon name="user" />
            {t('cal.people.me')}
          </button>
        )}
        {department.length > 1 && (
          <button type="button" className="chip" aria-pressed={isDepartment} onClick={() => onChange(department)}>
            <Icon name="hr" />
            {t('cal.people.department')} <span className="count">{department.length}</span>
          </button>
        )}
        {viewable.length > 1 && (
          <div className="cal-people">
            <button type="button" className="chip" aria-expanded={open} onClick={() => setOpen((was) => !was)}>
              {t('cal.people.pick')} <span className="count">{chosen.length}</span>
              <Icon name="chevron" />
            </button>
            {open && (
              <div className="cal-people-pop" role="dialog" aria-label={t('cal.people.pick')}>
                <input className="input input-sm" placeholder={t('cal.people.filter')} value={filter}
                       aria-label={t('cal.people.filter')} onChange={(event) => setFilter(event.target.value)} />
                <ul className="cal-pick">
                  {viewable
                    .filter((person) => !needle || (person.name ?? '').toLowerCase().includes(needle)
                      || (person.departmentName ?? '').toLowerCase().includes(needle))
                    .map((person) => (
                      <li key={person.id}>
                        <label className="row">
                          <input
                            type="checkbox"
                            checked={chosen.includes(person.id)}
                            onChange={() => onChange(chosen.includes(person.id)
                              ? chosen.filter((id) => id !== person.id)
                              : [...chosen, person.id])}
                          />
                          <span className="ellipsis">{person.name ?? person.id}</span>
                          {person.departmentName && <span className="t-sm muted ellipsis">{person.departmentName}</span>}
                        </label>
                      </li>
                    ))}
                </ul>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>{t('cal.people.done')}</button>
              </div>
            )}
          </div>
        )}
        {people?.scope === 'own' && <span className="t-sm muted">{t('cal.people.ownOnly')}</span>}
      </div>
      {chosen.length > 1 && (
        <div className="cal-legend" aria-label={t('cal.people.legend')}>
          {chosen.map((id) => (
            <span key={id}><span className="dot" style={{ background: colorOf(id) }} />{names.get(id) ?? id}</span>
          ))}
        </div>
      )}
    </div>
  )
}

/** Цвет человека на сетке: несколько календарей сразу различаются по полоске слева. */
function useColors(chosen: string[]): (id: string) => string {
  return useMemo(() => {
    const palette = ['var(--accent)', 'var(--success)', 'var(--warning)', 'var(--info)', 'var(--danger)', 'var(--fg-muted)']
    const index = new Map(chosen.map((id, position) => [id, position]))
    return (id: string) => palette[(index.get(id) ?? 0) % palette.length] ?? 'var(--accent)'
  }, [chosen])
}

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id))
}

// ------------------------------------------------------------------ день и неделя

/** Положение события в дне: начало и длина в минутах, дорожка среди пересекающихся. */
interface Placed {
  event: CalendarEvent
  top: number
  height: number
  lane: number
  lanes: number
}

function layoutDay(day: Date, events: CalendarEvent[]): Placed[] {
  const dayStart = startOfDay(day).getTime()
  const dayEnd = addDays(startOfDay(day), 1).getTime()
  const items = events
    .map((event) => {
      const start = new Date(event.start).getTime()
      const end = event.endsAt ? new Date(event.endsAt).getTime() : start + MIN_VISIBLE_MINUTES * 60_000
      // A block shorter than two lines (title, time) is a strip nobody can read; the visible
      // length also decides the lanes, or a short event would be drawn over its neighbour.
      return { event, start, end: Math.max(end, start + MIN_VISIBLE_MINUTES * 60_000) }
    })
    .filter((item) => item.start < dayEnd && item.end > dayStart)
    .sort((a, b) => a.start - b.start || b.end - a.end)

  // Пересекающиеся события делят ширину колонки: жадно по дорожкам внутри группы.
  const placed: Placed[] = []
  let group: { item: (typeof items)[number]; lane: number }[] = []
  let groupEnd = -Infinity
  const flush = () => {
    const lanes = Math.max(1, ...group.map((entry) => entry.lane + 1))
    for (const entry of group) {
      const from = Math.max(entry.item.start, dayStart)
      const to = Math.min(entry.item.end, dayEnd)
      placed.push({
        event: entry.item.event,
        top: (from - dayStart) / 60_000,
        height: Math.max((to - from) / 60_000, MIN_VISIBLE_MINUTES),
        lane: entry.lane,
        lanes,
      })
    }
    group = []
  }
  for (const item of items) {
    if (item.start >= groupEnd) {
      flush()
      groupEnd = -Infinity
    }
    const laneEnds: number[] = []
    for (const entry of group) laneEnds[entry.lane] = Math.max(laneEnds[entry.lane] ?? -Infinity, entry.item.end)
    let lane = laneEnds.findIndex((end) => end <= item.start)
    if (lane < 0) lane = laneEnds.length
    group.push({ item, lane })
    groupEnd = Math.max(groupEnd, item.end)
  }
  flush()
  return placed
}

function TimeGrid({
  days,
  events,
  selectedId,
  several,
  colorOf,
  onSelect,
  onSlot,
  onDrop,
}: {
  days: Date[]
  events: CalendarEvent[]
  selectedId: string | null
  several: boolean
  colorOf: (id: string) => string
  onSelect: (id: string) => void
  onSlot?: (at: Date) => void
  onDrop: (id: string, at: Date) => void
}) {
  const { locale } = useApp()
  const scroller = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const now = new Date()

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = SCROLL_TO_HOUR * HOUR_PX
  }, [])

  const slots = Array.from({ length: MINUTES_IN_DAY / SNAP_MINUTES }, (_, index) => index * SNAP_MINUTES)

  return (
    <div className={dragging ? 'cal-grid is-dragging' : 'cal-grid'} style={{ ['--hour' as string]: `${HOUR_PX}px` }}>
      <div className="cal-cols" style={{ ['--days' as string]: days.length }}>
        <div />
        {days.map((day) => (
          <div key={day.toISOString()} className={sameDay(day, now) ? 'cal-dayhead is-today' : 'cal-dayhead'}>
            <span>{format(locale, { weekday: 'short' }).format(day)}</span>
            <span className="num">{day.getDate()}</span>
          </div>
        ))}
      </div>
      <div className="cal-scroll" ref={scroller}>
        <div className="cal-cols cal-hours" style={{ ['--days' as string]: days.length }}>
          <div>
            {Array.from({ length: 24 }, (_, hour) => (
              <div key={hour} className="cal-hour-label">{hour === 0 ? '' : `${String(hour).padStart(2, '0')}:00`}</div>
            ))}
          </div>
          {days.map((day) => {
            const dayKey = toLocalInput(day).slice(0, 10)
            const placed = layoutDay(day, events)
            return (
              <div key={dayKey} className={sameDay(day, now) ? 'cal-col is-today' : 'cal-col'} data-day={dayKey}>
                {slots.map((minutes) => {
                  const at = atMinutes(day, minutes)
                  const key = toLocalInput(at)
                  return (
                    <button
                      key={minutes}
                      type="button"
                      tabIndex={-1}
                      className={over === key ? 'cal-slot is-over' : 'cal-slot'}
                      style={{ top: (minutes / SNAP_MINUTES) * SLOT_PX }}
                      data-slot={key}
                      // Mouse-only shortcut: the keyboard path is the "Task" and "Meeting"
                      // buttons in the header. 672 labelled buttons per week would also make
                      // a screen reader's list of controls unusable.
                      aria-hidden="true"
                      onClick={() => onSlot?.(at)}
                      onDragOver={(event) => {
                        if (!dragging) return
                        event.preventDefault()
                        if (over !== key) setOver(key)
                      }}
                      onDrop={(event) => {
                        event.preventDefault()
                        const id = event.dataTransfer?.getData('text/plain') || dragging
                        setDragging(null)
                        setOver(null)
                        if (id) onDrop(id, at)
                      }}
                    />
                  )
                })}
                {sameDay(day, now) && (
                  <div className="cal-now" style={{ top: (minutesOfDay(now) / 60) * HOUR_PX }} aria-hidden="true" />
                )}
                {placed.map((item) => (
                  <EventBlock
                    key={item.event.id}
                    event={item.event}
                    selected={item.event.id === selectedId}
                    several={several}
                    colorOf={colorOf}
                    style={{
                      top: (item.top / 60) * HOUR_PX,
                      height: (item.height / 60) * HOUR_PX - 2,
                      left: `calc(${(100 / item.lanes) * item.lane}% + 2px)`,
                      width: `calc(${100 / item.lanes}% - 4px)`,
                    }}
                    onSelect={onSelect}
                    onDragStart={(id) => setDragging(id)}
                    onDragEnd={() => { setDragging(null); setOver(null) }}
                  />
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function EventBlock({
  event,
  selected,
  several,
  colorOf,
  style,
  onSelect,
  onDragStart,
  onDragEnd,
}: {
  event: CalendarEvent
  selected: boolean
  several: boolean
  colorOf: (id: string) => string
  style?: React.CSSProperties
  onSelect: (id: string) => void
  onDragStart: (id: string) => void
  onDragEnd: () => void
}) {
  const { t, locale } = useApp()
  const done = event.status === 'done'
  const classes = [
    'cal-ev',
    event.kind === 'task' ? 'is-task' : event.kind === 'call' ? 'is-call' : 'is-meeting',
    done ? 'is-done' : '',
    event.overdue ? 'is-overdue' : '',
    event.conflict ? 'is-conflict' : '',
    selected ? 'is-selected' : '',
  ].filter(Boolean).join(' ')
  const owner = event.employeeIds[0] ?? event.assigneeEmployeeId ?? null
  const start = new Date(event.start)
  const range = event.endsAt ? `${time(start, locale)}–${time(new Date(event.endsAt), locale)}` : time(start, locale)
  return (
    <button
      type="button"
      className={classes}
      style={several && owner ? { ...style, borderLeft: `3px solid ${colorOf(owner)}` } : style}
      draggable={event.editable}
      aria-pressed={selected}
      aria-label={eventLabel(event, locale, t)}
      onClick={() => onSelect(event.id)}
      onDragStart={(drag) => {
        drag.dataTransfer?.setData('text/plain', event.id)
        if (drag.dataTransfer) drag.dataTransfer.effectAllowed = 'move'
        onDragStart(event.id)
      }}
      onDragEnd={onDragEnd}
    >
      <span className="t ellipsis">
        {event.conflict && <Icon name="alert" size="xs" />} {event.kind === 'task' && <Icon name="check" size="xs" />} {event.subject}
      </span>
      <span className="s ellipsis">
        {range}
        {several && event.assigneeName ? ` · ${event.assigneeName}` : ''}
        {event.location ? ` · ${event.location}` : ''}
      </span>
    </button>
  )
}

// ------------------------------------------------------------------ месяц

function MonthGrid({
  days,
  anchor,
  events,
  selectedId,
  several,
  colorOf,
  onSelect,
  onDay,
  onDrop,
}: {
  days: Date[]
  anchor: Date
  events: CalendarEvent[]
  selectedId: string | null
  several: boolean
  colorOf: (id: string) => string
  onSelect: (id: string) => void
  onDay: (day: Date) => void
  onDrop: (id: string, day: Date) => void
}) {
  const { t, locale } = useApp()
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const now = new Date()
  const weekdays = days.slice(0, 7)
  return (
    <div className={dragging ? 'cal-grid is-dragging' : 'cal-grid'}>
      <div className="cal-month">
        {weekdays.map((day) => (
          <div key={`wd-${day.getDay()}`} className="cal-wd">
            {format(locale, { weekday: 'short' }).format(day)}
          </div>
        ))}
        {days.map((day) => {
          const key = toLocalInput(day).slice(0, 10)
          const dayEvents = events.filter((event) => sameDay(new Date(event.start), day))
          const shown = dayEvents.slice(0, 3)
          const classes = [
            'cal-cell',
            day.getMonth() !== anchor.getMonth() ? 'is-other' : '',
            sameDay(day, now) ? 'is-today' : '',
            over === key ? 'is-over' : '',
          ].filter(Boolean).join(' ')
          return (
            <div
              key={key}
              className={classes}
              data-cell={key}
              onDragOver={(event) => {
                if (!dragging) return
                event.preventDefault()
                if (over !== key) setOver(key)
              }}
              onDrop={(event) => {
                event.preventDefault()
                const id = event.dataTransfer?.getData('text/plain') || dragging
                setDragging(null)
                setOver(null)
                if (id) onDrop(id, day)
              }}
            >
              <button type="button" className="cal-cell-num" aria-label={t('cal.openDay', { when: dayLabel(day, locale) })}
                      onClick={() => onDay(day)}>
                {day.getDate()}
              </button>
              {shown.map((event) => (
                <EventBlock
                  key={event.id}
                  event={event}
                  selected={event.id === selectedId}
                  several={several}
                  colorOf={colorOf}
                  onSelect={onSelect}
                  onDragStart={(id) => setDragging(id)}
                  onDragEnd={() => { setDragging(null); setOver(null) }}
                />
              ))}
              {dayEvents.length > shown.length && (
                <button type="button" className="cal-more btn btn-ghost btn-sm" onClick={() => onDay(day)}>
                  {t('cal.more', { count: dayEvents.length - shown.length })}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ выбранное событие

function EventDetails({
  event,
  me,
  busy,
  onClose,
  onMove,
  onEdit,
  onComplete,
}: {
  event: CalendarEvent
  me: string | null
  busy: boolean
  onClose: () => void
  onMove: (at: Date) => void
  onEdit: () => void
  onComplete: () => void
}) {
  const { t, locale } = useApp()
  const [moveTo, setMoveTo] = useState(() => toLocalInput(new Date(event.start)))
  useEffect(() => setMoveTo(toLocalInput(new Date(event.start))), [event.id, event.start])
  const open = event.status === 'planned' || event.status === 'in_progress'
  const start = new Date(event.start)
  const base = event.entityType ? listPath(catalogName(event.entityType), '') : ''
  const target = fromLocalInput(moveTo)
  return (
    <aside className="panel" aria-labelledby="cal-ev-h">
      <div className="panel-h" style={{ alignItems: 'flex-start', paddingTop: 10, paddingBottom: 10 }}>
        <div className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
          <span className="overline">{t(`cal.kind.${event.kind}`)}</span>
          <span className="h2" id="cal-ev-h">{event.subject}</span>
        </div>
        <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('cal.details.close')} onClick={onClose}>
          <Icon name="close" size="sm" />
        </button>
      </div>
      <div className="panel-b stack" style={{ gap: 12 }}>
        {event.conflict && (
          <div className="alert alert-warning" role="status">
            <Icon name="alert" />
            <span className="fg">{t('cal.conflict')}</span>
          </div>
        )}
        <dl className="kv" style={{ gridTemplateColumns: '100px minmax(0, 1fr)' }}>
          <dt>{event.kind === 'task' ? t('cal.form.due') : t('cal.when')}</dt>
          <dd>
            {dayLabel(start, locale)}, {time(start, locale)}
            {event.endsAt && `–${time(new Date(event.endsAt), locale)}`}
            {event.overdue && <> <span className="flag flag-danger">{t('tasks.overdueFlag')}</span></>}
          </dd>
          <dt>{t('cal.status')}</dt>
          <dd>{t(`tasks.status.${event.status}`)}</dd>
          {event.location && (
            <>
              <dt>{t('cal.form.location')}</dt>
              <dd className="ellipsis">{event.location}</dd>
            </>
          )}
          {event.assigneeName && (
            <>
              <dt>{event.kind === 'meeting' ? t('cal.organizer') : t('cal.form.assignee')}</dt>
              <dd>{event.assigneeName}</dd>
            </>
          )}
          <dt>{t('cal.record')}</dt>
          <dd>
            {event.entityType && event.entityId ? (
              base ? (
                <Link to={`${base}/${event.entityId}`}>
                  {t(`myday.entity.${event.entityType}`)}{event.entityLabel ? ` · ${event.entityLabel}` : ''}
                </Link>
              ) : (
                <span>{t(`myday.entity.${event.entityType}`)}</span>
              )
            ) : (
              <span className="muted">{t('cal.noRecord')}</span>
            )}
          </dd>
          <dt>{t('cal.form.remind')}</dt>
          <dd>{event.remindBeforeMinutes == null ? t('cal.remind.none') : t('cal.remind.before', { minutes: event.remindBeforeMinutes })}</dd>
          {event.recurrence && (
            <>
              <dt>{t('cal.form.repeat')}</dt>
              <dd>
                {t(`cal.repeat.${event.recurrence}`)}
                {event.recurrenceUntil ? ` · ${t('cal.repeat.until', { date: event.recurrenceUntil })}` : ''}
              </dd>
            </>
          )}
        </dl>
        {event.participants.length > 0 && (
          <div className="stack" style={{ gap: 4 }}>
            <span className="overline">{t('cal.participants')}</span>
            <ul className="cal-pick" style={{ maxHeight: 'none' }}>
              {event.participants.map((participant) => (
                <li key={participant.employeeId ?? participant.contactId}>
                  <span className="row t-sm">
                    <Icon name={participant.employeeId ? 'user' : 'building'} size="xs" />
                    {participant.name ?? t('cal.contact.hidden')}
                    <span className="muted">{participant.employeeId ? t('cal.participant.employee') : t('cal.participant.contact')}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {event.body && <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{event.body}</p>}
        {event.editable && open && (
          <form
            className="stack"
            style={{ gap: 6 }}
            onSubmit={(submit) => {
              submit.preventDefault()
              if (target) onMove(target)
            }}
          >
            <label className="label" htmlFor="cal-move-to">{t('cal.moveTo')}</label>
            <div className="row">
              <input id="cal-move-to" className="input input-sm" type="datetime-local" value={moveTo}
                     onChange={(change) => setMoveTo(change.target.value)} />
              <button type="submit" className="btn btn-secondary btn-sm" disabled={busy || !target}>{t('cal.move')}</button>
            </div>
            <span className="help">{t('cal.moveTo.help')}</span>
          </form>
        )}
        {!event.editable && open && <span className="t-sm muted">{t('cal.readOnly')}</span>}
      </div>
      {open && (event.editable || event.assigneeEmployeeId === me) && (
        <div className="panel-f" style={{ padding: '10px 14px' }}>
          <span className="spacer" />
          {event.editable && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={onEdit}>
              <Icon name="edit" size="sm" />
              {t('cal.edit')}
            </button>
          )}
          {event.kind === 'task' && event.assigneeEmployeeId === me && (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={onComplete}>
              <Icon name="check" size="sm" />
              {t('tasks.complete')}
            </button>
          )}
        </div>
      )}
    </aside>
  )
}

// ------------------------------------------------------------------ подписи

function catalogName(entityType: string): string {
  switch (entityType) {
    case 'invoice':
      return 'billing.invoice'
    default:
      return `sales.${entityType}`
  }
}

function eventLabel(event: CalendarEvent, locale: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const start = new Date(event.start)
  const parts = [t(`cal.kind.${event.kind}`), event.subject, `${dayLabel(start, locale)} ${time(start, locale)}`]
  if (event.assigneeName) parts.push(event.assigneeName)
  if (event.conflict) parts.push(t('cal.conflict.short'))
  if (event.overdue) parts.push(t('tasks.overdueBadge'))
  return parts.join(', ')
}

function dayLabel(date: Date, locale: string): string {
  return format(locale, { weekday: 'short', day: 'numeric', month: 'long' }).format(date)
}

function slotLabel(date: Date, locale: string): string {
  return `${dayLabel(date, locale)}, ${time(date, locale)}`
}

function periodLabel(view: CalendarView, anchor: Date, days: Date[], locale: string): string {
  if (view === 'day') {
    return format(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(anchor)
  }
  if (view === 'month') {
    return format(locale, { month: 'long', year: 'numeric' }).format(anchor)
  }
  const first = days[0] ?? anchor
  const last = days[days.length - 1] ?? anchor
  const short = format(locale, { day: 'numeric', month: 'short' })
  return `${short.format(first)} – ${short.format(last)} ${last.getFullYear()}`
}
